import { ArrowRight, Check, Clock3, LockKeyhole, MessageSquareText, Phone } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { api } from "../lib/api";
import scgLogoUrl from "../assets/scg-logo.png";
import { readableError } from "../lib/format";

type FormData = { firstName: string; lastName: string; phone: string; reason: string; website: string };
const emptyForm: FormData = { firstName: "", lastName: "", phone: "", reason: "", website: "" };

export function PublicRequestPage({ navigate }: { navigate: (path: string) => void }) {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [lastSubmission, setLastSubmission] = useState(() => Number(localStorage.getItem("scg-request-submitted-at") || 0));
  const [cooldown, setCooldown] = useState(() => Date.now() - lastSubmission < 60_000);

  useEffect(() => {
    document.title = "Beratung anfragen · SCG";
  }, []);

  useEffect(() => {
    const remaining = 60_000 - (Date.now() - lastSubmission);
    setCooldown(remaining > 0);
    if (remaining <= 0) return;
    const timer = window.setTimeout(() => setCooldown(false), remaining);
    return () => window.clearTimeout(timer);
  }, [lastSubmission]);

  function update(field: keyof FormData, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (submitting || cooldown) return;
    setSubmitting(true);
    setError("");
    try {
      await api.submitRequest(form);
      const now = Date.now();
      localStorage.setItem("scg-request-submitted-at", String(now));
      setLastSubmission(now);
      setSubmitted(true);
      setForm(emptyForm);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (cause) {
      setError(readableError(cause));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="public-page">
      <header className="public-header">
        <a className="brand" href="/" onClick={(event) => { event.preventDefault(); navigate("/"); }}>
          <img className="brand-logo" src={scgLogoUrl} alt="SCG Logo" />
          <span><strong>Sophie-Charlotte-Gymnasium</strong><small>Oberstufenkoordination</small></span>
        </a>
        <button className="admin-link" type="button" onClick={() => navigate("/admin")}><LockKeyhole size={15} /> Verwaltung</button>
      </header>

      <main className="public-main">
        <section className="public-intro">
          <div className="intro-kicker"><span /> Beratung in der Oberstufe</div>
          <h1>Manche Fragen klären sich besser <em>im Gespräch.</em></h1>
          <p className="intro-copy">Sende uns deine Kontaktdaten. Die Oberstufenkoordination plant deinen Termin und informiert dich anschließend per SMS.</p>
          <div className="process-list" aria-label="Ablauf">
            <div><span>01</span><p><strong>Anfrage senden</strong><small>Nur deine Kontaktdaten sind nötig.</small></p></div>
            <div><span>02</span><p><strong>Termin wird geplant</strong><small>Deine Anfrage ist noch keine Terminzusage.</small></p></div>
            <div><span>03</span><p><strong>SMS erhalten</strong><small>Datum und Uhrzeit kommen auf dein Handy.</small></p></div>
          </div>
          <div className="privacy-note"><LockKeyhole size={17} /><p><strong>Vertraulich behandelt</strong><br />Deine Angaben sieht nur die Oberstufenkoordination und sie werden nach dem Beratungszeitraum gelöscht.</p></div>
        </section>

        <section className="request-card" aria-labelledby="form-title">
          {submitted ? (
            <div className="success-state" role="status">
              <div className="success-icon"><Check size={28} /></div>
              <p className="eyebrow">Anfrage angekommen</p>
              <h2>Danke, wir kümmern uns darum.</h2>
              <p>Deine Anfrage wurde übermittelt. Du erhältst eine SMS, sobald dir ein Beratungstermin zugeteilt wurde.</p>
              <div className="success-reminder"><Phone size={18} /><span>Bitte behalte dein Handy in den nächsten Tagen im Blick.</span></div>
              <button className="button button--secondary" type="button" onClick={() => setSubmitted(false)}>Weitere Anfrage senden</button>
            </div>
          ) : (
            <>
              <div className="card-heading">
                <p className="eyebrow">Unverbindliche Anfrage</p>
                <h2 id="form-title">Wie können wir dich erreichen?</h2>
                <p>Alle mit * markierten Felder sind erforderlich.</p>
              </div>
              <form onSubmit={submit} className="request-form">
                <div className="field-row">
                  <label><span>Vorname *</span><input autoComplete="given-name" required maxLength={60} value={form.firstName} onChange={(event) => update("firstName", event.target.value)} placeholder="Dein Vorname" /></label>
                  <label><span>Nachname *</span><input autoComplete="family-name" required maxLength={60} value={form.lastName} onChange={(event) => update("lastName", event.target.value)} placeholder="Dein Nachname" /></label>
                </div>
                <label><span>Handynummer *</span><div className="input-with-icon"><Phone size={18} /><input type="tel" autoComplete="tel" required minLength={6} maxLength={30} value={form.phone} onChange={(event) => update("phone", event.target.value)} placeholder="z. B. 0176 12345678" /></div><small>Wir verwenden die Nummer nur für die Termin-SMS.</small></label>
                <label><span>Beratungsgrund <i>optional</i></span><div className="textarea-wrap"><MessageSquareText size={18} /><textarea rows={4} maxLength={500} value={form.reason} onChange={(event) => update("reason", event.target.value)} placeholder="Wenn du möchtest, kannst du kurz angeben, worum es gehen soll." /></div><small className="character-count">{form.reason.length} / 500</small></label>
                <label className="honeypot" aria-hidden="true">Website<input tabIndex={-1} autoComplete="off" value={form.website} onChange={(event) => update("website", event.target.value)} /></label>
                {error && <div className="form-error" role="alert">{error}</div>}
                {cooldown && <div className="form-info" role="status"><Clock3 size={17} /> Deine Anfrage wurde bereits übermittelt. Bitte warte kurz, bevor du eine weitere sendest.</div>}
                <button className="button button--primary button--full" type="submit" disabled={submitting || cooldown}>
                  {submitting ? <span className="spinner" /> : <>Anfrage absenden <ArrowRight size={19} /></>}
                </button>
                <p className="consent-copy">Mit dem Absenden stimmst du der Verarbeitung deiner Angaben zur Terminorganisation zu.</p>
              </form>
            </>
          )}
        </section>
      </main>
      <footer className="public-footer"><span>© {new Date().getFullYear()} Sophie-Charlotte-Gymnasium Berlin</span><span>Beratung · sicher organisiert</span></footer>
    </div>
  );
}
